import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  CalendarDays, 
  MapPin, 
  Phone, 
  Mail, 
  Plane, 
  Hotel, 
  Users, 
  Star,
  Check,
  ArrowRight,
  Globe,
  Shield,
  Clock,
  Calendar as CalendarIcon
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const bookingSchema = z.object({
  client_name: z.string().min(2, "الاسم مطلوب"),
  client_phone: z.string().min(10, "رقم الجوال مطلوب"),
  client_email: z.string().email("البريد الإلكتروني غير صحيح").optional().or(z.literal("")),
  destination: z.string().min(1, "الوجهة مطلوبة"),
  start_date: z.date({ required_error: "تاريخ البداية مطلوب" }),
  end_date: z.date({ required_error: "تاريخ النهاية مطلوب" }),
  passengers_count: z.number().min(1, "عدد المسافرين مطلوب"),
  accommodation_type: z.string().min(1, "نوع الإقامة مطلوب"),
  special_requests: z.string().optional(),
});

type BookingFormData = z.infer<typeof bookingSchema>;

interface TourPackage {
  id: string;
  title: string;
  destination: string;
  duration: string;
  price: number;
  currency: string;
  image: string;
  features: string[];
  rating: number;
}

const featuredPackages: TourPackage[] = [
  {
    id: "1",
    title: "رحلة إلى دبي",
    destination: "دبي، الإمارات العربية المتحدة",
    duration: "5 أيام / 4 ليالي",
    price: 2500,
    currency: "SAR",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    features: ["طيران ذهاب وإياب", "إقامة 4 نجوم", "إفطار يومي", "جولات سياحية"],
    rating: 4.8,
  },
  {
    id: "2",
    title: "جولة في تركيا",
    destination: "إسطنبول، تركيا",
    duration: "7 أيام / 6 ليالي",
    price: 3200,
    currency: "SAR",
    image: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    features: ["طيران ذهاب وإياب", "إقامة 5 نجوم", "جميع الوجبات", "مرشد سياحي"],
    rating: 4.9,
  },
  {
    id: "3",
    title: "عمرة مع الإقامة",
    destination: "مكة المكرمة والمدينة المنورة",
    duration: "10 أيام / 9 ليالي",
    price: 4500,
    currency: "SAR",
    image: "https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    features: ["طيران ذهاب وإياب", "إقامة قريبة من الحرم", "وجبات", "نقل مكيف"],
    rating: 5.0,
  },
];

export default function PublicBookingSystem() {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { toast } = useToast();

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      client_name: "",
      client_phone: "",
      client_email: "",
      destination: "",
      passengers_count: 1,
      accommodation_type: "",
      special_requests: "",
    },
  });

  const onSubmit = async (data: BookingFormData) => {
    setIsSubmitting(true);
    try {
      // Create a booking record
      const { error } = await supabase.from("package_bookings").insert({
        package_number: `PUB-${Date.now()}`,
        price: selectedPackage ? featuredPackages.find(p => p.id === selectedPackage)?.price || 0 : 0,
        currency: "SAR",
        booking_date: new Date().toISOString().split('T')[0],
        start_date: data.start_date.toISOString().split('T')[0],
        end_date: data.end_date.toISOString().split('T')[0],
        status: "pending",
        payment_status: "unpaid",
        notes: `
العميل: ${data.client_name}
الهاتف: ${data.client_phone}
البريد الإلكتروني: ${data.client_email || "غير محدد"}
الوجهة: ${data.destination}
عدد المسافرين: ${data.passengers_count}
نوع الإقامة: ${data.accommodation_type}
الطلبات الخاصة: ${data.special_requests || "لا توجد"}
الحزمة المختارة: ${selectedPackage ? featuredPackages.find(p => p.id === selectedPackage)?.title : "حجز مخصص"}
تاريخ الحجز العام: ${new Date().toLocaleString('ar-SA')}
        `.trim(),
        client_id: "00000000-0000-0000-0000-000000000000", // Temporary client ID for public bookings
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "تم إرسال طلب الحجز بنجاح",
        description: "سيتم التواصل معك قريباً لتأكيد الحجز والدفع.",
      });
    } catch (error: any) {
      console.error("Error submitting booking:", error);
      toast({
        title: "خطأ في إرسال الحجز",
        description: error.message || "حدث خطأ أثناء إرسال طلب الحجز",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-700">تم إرسال طلب الحجز!</CardTitle>
            <CardDescription className="text-lg">
              شكراً لك على اختيار خدماتنا. سيتم التواصل معك خلال 24 ساعة لتأكيد الحجز.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => {
                setIsSubmitted(false);
                setCurrentStep(1);
                setSelectedPackage(null);
                form.reset();
              }}
              variant="outline"
              className="w-full"
            >
              حجز جديد
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            اكتشف العالم معنا
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            رحلات سياحية استثنائية بأفضل الأسعار وأعلى معايير الجودة
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span>ضمان الجودة</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>خدمة 24/7</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              <span>وجهات عالمية</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4">
        {/* Featured Packages */}
        {currentStep === 1 && (
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">الحزم السياحية المميزة</h2>
              <p className="text-muted-foreground text-lg">
                اختر من حزمنا المميزة أو احجز رحلة مخصصة حسب رغبتك
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {featuredPackages.map((pkg) => (
                <Card 
                  key={pkg.id} 
                  className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                    selectedPackage === pkg.id ? 'ring-2 ring-primary shadow-lg' : ''
                  }`}
                  onClick={() => {
                    setSelectedPackage(pkg.id);
                    form.setValue('destination', pkg.destination);
                  }}
                >
                  <div className="relative">
                    <img 
                      src={pkg.image} 
                      alt={pkg.title}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        {pkg.rating}
                      </Badge>
                    </div>
                    {selectedPackage === pkg.id && (
                      <div className="absolute inset-0 bg-primary/20 rounded-t-lg flex items-center justify-center">
                        <div className="bg-primary text-primary-foreground rounded-full p-2">
                          <Check className="w-6 h-6" />
                        </div>
                      </div>
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{pkg.title}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {pkg.destination}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{pkg.duration}</span>
                        <div className="text-lg font-bold text-primary">
                          {pkg.price.toLocaleString()} {pkg.currency}
                        </div>
                      </div>
                      <div className="space-y-1">
                        {pkg.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            <Check className="w-3 h-3 text-green-600" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center space-y-4">
              <Button 
                onClick={() => setCurrentStep(2)}
                size="lg"
                className="min-w-[200px]"
                disabled={!selectedPackage}
              >
                {selectedPackage ? 'متابعة الحجز' : 'اختر حزمة أولاً'}
                <ArrowRight className="w-4 h-4 mr-2" />
              </Button>
              <div>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedPackage("custom");
                    setCurrentStep(2);
                  }}
                >
                  أو احجز رحلة مخصصة
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Form */}
        {currentStep === 2 && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">إكمال بيانات الحجز</h2>
              <p className="text-muted-foreground">
                {selectedPackage && selectedPackage !== "custom" 
                  ? `حجز: ${featuredPackages.find(p => p.id === selectedPackage)?.title}`
                  : "حجز رحلة مخصصة"
                }
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  تفاصيل الحجز
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    {/* معلومات العميل */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">معلومات الاتصال</h3>
                      
                      <FormField
                        control={form.control}
                        name="client_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>الاسم الكامل *</FormLabel>
                            <FormControl>
                              <Input placeholder="أدخل اسمك الكامل" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="client_phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                رقم الجوال *
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="05xxxxxxxx" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="client_email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                البريد الإلكتروني
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="example@email.com" type="email" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* تفاصيل الرحلة */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">تفاصيل الرحلة</h3>
                      
                      {selectedPackage === "custom" && (
                        <FormField
                          control={form.control}
                          name="destination"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                الوجهة المرغوبة *
                              </FormLabel>
                              <FormControl>
                                <Input placeholder="أدخل الوجهة المرغوبة" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="start_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>تاريخ البداية *</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={`w-full pl-3 text-left font-normal ${
                                        !field.value && "text-muted-foreground"
                                      }`}
                                    >
                                      {field.value ? (
                                        format(field.value, "PPP", { locale: ar })
                                      ) : (
                                        <span>اختر تاريخ البداية</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={field.value}
                                    onSelect={field.onChange}
                                    disabled={(date) => date < new Date()}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="end_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>تاريخ النهاية *</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={`w-full pl-3 text-left font-normal ${
                                        !field.value && "text-muted-foreground"
                                      }`}
                                    >
                                      {field.value ? (
                                        format(field.value, "PPP", { locale: ar })
                                      ) : (
                                        <span>اختر تاريخ النهاية</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={field.value}
                                    onSelect={field.onChange}
                                    disabled={(date) => 
                                      date < new Date() || 
                                      (form.getValues('start_date') && date <= form.getValues('start_date'))
                                    }
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="passengers_count"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                عدد المسافرين *
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  min="1" 
                                  placeholder="1"
                                  {...field}
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="accommodation_type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Hotel className="w-4 h-4" />
                                نوع الإقامة *
                              </FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="اختر نوع الإقامة" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="3-star">فندق 3 نجوم</SelectItem>
                                  <SelectItem value="4-star">فندق 4 نجوم</SelectItem>
                                  <SelectItem value="5-star">فندق 5 نجوم</SelectItem>
                                  <SelectItem value="resort">منتجع</SelectItem>
                                  <SelectItem value="apartment">شقة مفروشة</SelectItem>
                                  <SelectItem value="villa">فيلا</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="special_requests"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>طلبات خاصة</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="أي طلبات خاصة أو ملاحظات (اختياري)"
                                className="min-h-[100px]"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex gap-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setCurrentStep(1)}
                        className="flex-1"
                      >
                        رجوع
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        {isSubmitting ? "جاري الإرسال..." : "إرسال طلب الحجز"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-muted mt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-right">
            <div>
              <h3 className="font-bold text-lg mb-4">معلومات الاتصال</h3>
              <div className="space-y-2 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+966 11 123 4567</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@example.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>الرياض، المملكة العربية السعودية</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">خدماتنا</h3>
              <div className="space-y-2 text-muted-foreground">
                <div>رحلات سياحية</div>
                <div>حجز فنادق</div>
                <div>تذاكر طيران</div>
                <div>رحلات العمرة</div>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">ساعات العمل</h3>
              <div className="space-y-2 text-muted-foreground">
                <div>السبت - الخميس: 9:00 ص - 6:00 م</div>
                <div>الجمعة: مغلق</div>
                <div>خدمة الطوارئ: 24/7</div>
              </div>
            </div>
          </div>
          <div className="border-t mt-8 pt-8 text-center text-muted-foreground">
            <p>&copy; 2024 جميع الحقوق محفوظة</p>
          </div>
        </div>
      </footer>
    </div>
  );
}